import type { UniqueIdentifier } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';

import type { FlattenedItem, TreeItem, TreeItems } from './types';

function getDragDepth(offset: number, indentationWidth: number) {
  return Math.round(offset / indentationWidth);
}

export function getProjection<T>(
  items: FlattenedItem<T>[],
  activeId: UniqueIdentifier | null,
  overId: UniqueIdentifier | null,
  dragOffset: number,
  indentationWidth: number,
): {
  depth: number;
  parentId: UniqueIdentifier | null;
  parent: FlattenedItem<T> | null;
  isLast: boolean;
} | null {
  if (!activeId || !overId) return null;

  const overItemIndex = items.findIndex(({ id }) => id === overId);
  const activeItemIndex = items.findIndex(({ id }) => id === activeId);
  const activeItem = items[activeItemIndex];

  const reorderedItems = arrayMove(items, activeItemIndex, overItemIndex);
  const previousItem = reorderedItems[overItemIndex - 1];
  const nextItem = reorderedItems[overItemIndex + 1];

  const dragDepth = getDragDepth(dragOffset, indentationWidth);
  const projectedDepth = activeItem.depth + dragDepth;

  const candidateParent = findParentWithDepth(projectedDepth - 1, previousItem);
  const validParent = findParentWhichCanHaveChildren(candidateParent, activeItem);

  if (validParent === undefined) return null;

  const maxAllowedDepth = (validParent?.depth ?? -1) + 1;
  const minAllowedDepth = nextItem?.depth ?? 0;

  if (minAllowedDepth > maxAllowedDepth) return null;

  let finalDepth = projectedDepth;
  if (finalDepth >= maxAllowedDepth) {
    finalDepth = maxAllowedDepth;
  } else if (finalDepth < minAllowedDepth) {
    finalDepth = minAllowedDepth;
  }

  const isLast = (nextItem?.depth ?? -1) < finalDepth;

  const originalParentIsLast = validParent?.isLast;
  if (validParent?.isLast) {
    validParent.isLast = false;
  }

  const parentId = calculateParentId(finalDepth, previousItem, reorderedItems, overItemIndex);

  if (validParent && originalParentIsLast !== undefined) {
    validParent.isLast = originalParentIsLast;
  }

  return {
    depth: finalDepth,
    parentId,
    parent: validParent,
    isLast,
  };
}

function findParentWithDepth<T>(
  targetDepth: number,
  prevItem?: FlattenedItem<T>,
): FlattenedItem<T> | null {
  if (!prevItem) return null;
  let current = prevItem;
  while (targetDepth < current.depth) {
    if (current.parent === null) return null;
    current = current.parent;
  }
  return current;
}

function findParentWhichCanHaveChildren<T>(
  parentItem: FlattenedItem<T> | null,
  dragItem: FlattenedItem<T>,
): FlattenedItem<T> | null {
  if (!parentItem) return null;
  const canHaveChildren =
    typeof parentItem.canHaveChildren === 'function'
      ? parentItem.canHaveChildren(dragItem)
      : parentItem.canHaveChildren;
  if (canHaveChildren === false) {
    return findParentWhichCanHaveChildren(parentItem.parent, dragItem);
  }
  return parentItem;
}

function calculateParentId<T>(
  depth: number,
  previousItem: FlattenedItem<T> | undefined,
  reorderedItems: FlattenedItem<T>[],
  overItemIndex: number,
): UniqueIdentifier | null {
  if (depth === 0 || !previousItem) {
    return null;
  }

  if (depth === previousItem.depth) {
    return previousItem.parentId;
  }

  if (depth > previousItem.depth) {
    return previousItem.id;
  }

  const parentAtDepth = reorderedItems
    .slice(0, overItemIndex)
    .reverse()
    .find((item) => item.depth === depth);

  return parentAtDepth?.parentId ?? null;
}

function flatten<T extends object>(
  items: TreeItems<T>,
  parentId: UniqueIdentifier | null = null,
  depth = 0,
  parent: FlattenedItem<T> | null = null,
): FlattenedItem<T>[] {
  const result: FlattenedItem<T>[] = [];
  for (let index = 0; index < items.length; index++) {
    const item = items[index];
    const flattenedItem: FlattenedItem<T> = {
      ...item,
      parentId,
      depth,
      index,
      isLast: items.length === index + 1,
      parent,
    };
    result.push(flattenedItem);
    const children = flatten(item.children ?? [], item.id, depth + 1, flattenedItem);
    result.push(...children);
  }
  return result;
}

export function flattenTree<T extends object>(items: TreeItems<T>): FlattenedItem<T>[] {
  return flatten(items);
}

export function buildTree<T extends object>(flattenedItems: FlattenedItem<T>[]): TreeItems<T> {
  const nodeMap = new Map<string, TreeItem<T>>();
  const rootChildren: TreeItem<T>[] = [];

  for (const item of flattenedItems) {
    const node: TreeItem<T> = {
      ...item,
      children: [],
      parent: null,
    };
    nodeMap.set(String(item.id), node);
  }

  for (const item of flattenedItems) {
    const node = nodeMap.get(String(item.id));
    if (!node) continue;

    const parentId = item.parentId;
    if (parentId) {
      const parent = nodeMap.get(String(parentId));
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(node);
      }
    } else {
      rootChildren.push(node);
    }
  }

  return rootChildren;
}

function countChildren<T>(items: TreeItem<T>[], count = 0): number {
  return items.reduce((acc, { children }) => {
    if (children?.length) {
      return countChildren(children, acc + 1);
    }
    return acc + 1;
  }, count);
}

function findItemDeep<T extends object>(
  items: TreeItems<T>,
  itemId: UniqueIdentifier,
): TreeItem<T> | undefined {
  for (const item of items) {
    const { id, children } = item;
    if (id === itemId) {
      return item;
    }
    if (children?.length) {
      const child = findItemDeep(children, itemId);
      if (child) {
        return child;
      }
    }
  }
  return undefined;
}

export function getChildCount<T extends object>(items: TreeItems<T>, id: UniqueIdentifier) {
  if (!id) {
    return 0;
  }
  const item = findItemDeep(items, id);
  return item ? countChildren(item.children ?? []) : 0;
}

export function removeChildrenOf<T>(items: FlattenedItem<T>[], parentIds: UniqueIdentifier[]) {
  const excludedIds = new Set(parentIds);

  return items.filter((item) => {
    if (item.parentId && excludedIds.has(item.parentId)) {
      if (item.children?.length) {
        excludedIds.add(item.id);
      }
      return false;
    }
    return true;
  });
}
